import { Link, usePage } from "@inertiajs/react"
import {
  Briefcase,
  CalendarCheck,
  ClipboardList,
  Database,
  FileBarChart2,
  FileSignature,
  Gauge,
  Inbox,
  KeyRound,
  Layers,
  LayoutGrid,
  PiggyBank,
  Settings,
  TrainFront,
  Users,
  Warehouse,
  Wrench,
} from "lucide-react"
import { useMemo } from "react"

import { NavFooter } from "@/components/nav-footer"
import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import {
  orgOrgSettingsPath,
  organizationsPath,
  settingsProfilePath,
} from "@/routes"
import type { NavItem, SharedProps } from "@/types"

import AppLogo from "./app-logo"
import { OrgLogo } from "./org-logo"
import { OrgSwitcher } from "./org-switcher"

export function AppSidebar() {
  const { props } = usePage<SharedProps>()

  const isSuperadmin = props.auth.user.role === "superadmin"

  // Dashboard + logo must stay in the org the user is currently viewing.
  // useOrgRoutes resolves to the *current* org's scoped dashboard
  // (/:org_slug/) instead of the bare /dashboard route, which redirects
  // through DashboardController and picks an org from the last_org_slug
  // cookie (falling back to orgs.first) — that's what switched multi-org
  // users into the wrong org.
  const routes = useOrgRoutes()
  const dashboardHref = routes.dashboardPath()

  // No current org = the user is on /organizations picking a tenant. Sidebar
  // nav items all assume an org context, so don't surface anything that would
  // route away from the picker.
  const noOrgContext = !props.organization?.slug

  // A grant-only principal (workspace/facility access, no org membership) has a null
  // org role. Org-wide nav items are hidden for them so they never see a dead
  // link to a page that would 403 — they get a clean shell scoped to their
  // facilities (Dashboard).
  const isOrgMember = !!props.organization?.role

  // Reports are visible to any member OR facility/workspace grantee — the pages
  // scope their data to the grantee's facilities server-side, so a grantee sees a
  // real (filtered) page, not a 403.
  const canViewReports = !!props.organization?.can_view_reports

  // Master Data is visible to any trusted editor (org editor+ or a
  // facility/workspace editor grant). Org-admin-only "Access" stays on is_admin.
  const canManageMasterData = !!props.organization?.can_manage_master_data

  const mainNavItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = []
    // Only inside an org — clicking Dashboard from /organizations would
    // otherwise route into a specific org's dashboard, which reads as the app
    // overriding the user's "I want to pick" intent.
    if (!noOrgContext) {
      items.push({
        title: "Dashboard",
        href: dashboardHref,
        icon: LayoutGrid,
        exact: true,
      })
    }
    // The operational surfaces. Org-wide by nature (the fleet is not scoped to
    // one facility), so they follow the same isOrgMember rule as the rest of
    // the org-wide nav — a grant-only principal keeps the clean Dashboard-only
    // shell rather than a row of links that would 403.
    if (!noOrgContext && isOrgMember) {
      items.push(
        { title: "Cars", href: routes.carsPath(), icon: TrainFront },
        {
          title: "Work Orders",
          href: routes.workOrdersPath(),
          icon: ClipboardList,
        },
        { title: "Inbox", href: routes.inboxPath(), icon: Inbox },
        {
          title: "Requal Program",
          href: routes.requalPath(),
          icon: CalendarCheck,
        },
        {
          title: "Lessors & Riders",
          href: routes.lessorsPath(),
          icon: FileSignature,
        },
        { title: "Shops", href: routes.shopsPath(), icon: Warehouse },
        { title: "Scorecards", href: routes.scorecardsPath(), icon: Gauge },
        { title: "Savings", href: routes.savingsPath(), icon: PiggyBank },
      )
    }
    if (!noOrgContext && canViewReports) {
      items.push({
        title: "Reports",
        href: routes.reportsPath(),
        icon: FileBarChart2,
      })
    }
    if (!noOrgContext && canManageMasterData) {
      items.push({
        title: "Master Data",
        href: routes.masterDataPath(),
        icon: Layers,
      })
    }
    if (!noOrgContext && props.organization?.is_admin) {
      // Access management is org-admin-only: members, scoped (facility/workspace)
      // grants, and workspace definitions.
      items.push({
        title: "Access",
        href: routes.accessPath(),
        icon: Users,
      })
    }
    return items
  }, [
    noOrgContext,
    dashboardHref,
    isOrgMember,
    canViewReports,
    canManageMasterData,
    props.organization?.is_admin,
    routes,
  ])

  const footerNavItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = []
    if (isSuperadmin) {
      // Purpose-built superadmin console (orgs + users + feature access) — an
      // Inertia page (internal link), unlike the Motor engine below.
      items.push({
        title: "Admin Console",
        href: "/admin/console",
        icon: Wrench,
      })
      // Motor::Admin is a mounted Rails engine, not an Inertia page —
      // mark it external so the footer uses a plain anchor, not <Link>.
      items.push({
        title: "Database Admin",
        href: "/admin",
        icon: Database,
        external: true,
      })
    }
    if (props.organization?.slug && isOrgMember) {
      items.push({
        title: "Organization",
        href: orgOrgSettingsPath(props.organization.slug),
        icon: Briefcase,
      })
    }
    // Org-scoped, read-only API tokens. Per-user (each member manages their
    // own); gated by the :api_tokens flag. Lives in the footer with the other
    // account-level surfaces rather than in the operational nav.
    if (props.organization?.slug && isOrgMember && props.features?.api_tokens) {
      items.push({
        title: "API Tokens",
        href: routes.apiTokensPath(),
        icon: KeyRound,
      })
    }
    items.push({
      title: "Settings",
      href: settingsProfilePath(),
      icon: Settings,
    })
    return items
  }, [
    isSuperadmin,
    isOrgMember,
    props.features?.api_tokens,
    props.organization,
    routes,
  ])

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link
                href={noOrgContext ? organizationsPath() : dashboardHref}
                prefetch
              >
                {props.organization?.logo_url ? (
                  <OrgLogo
                    logoUrl={props.organization.logo_url}
                    logoIconUrl={props.organization.logo_icon_url}
                    orgName={props.organization.name}
                  />
                ) : (
                  <AppLogo />
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={mainNavItems} />
      </SidebarContent>

      <SidebarFooter>
        <NavFooter items={footerNavItems} className="mt-auto" />
        <OrgSwitcher />
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  )
}
